/**
 * ConsoleDisplay - Real-time monospace console output component
 *
 * Features:
 * - Black/dark gray background
 * - Green or white monospace text
 * - Auto-scroll to bottom as data arrives
 * - Optional line numbers
 * - Copy to clipboard functionality
 */

import { Check, Copy, Terminal, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ConsoleDisplayProps {
  /** Array of log lines to display */
  logs: string[];
  /** Console height (default: 300px) */
  height?: number | string;
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Text color variant */
  textColor?: 'white' | 'green' | 'amber';
  /** Background color variant */
  bgColor?: 'black' | 'gray' | 'dark';
  /** Title for the console header */
  title?: string;
  /** Called when clear button is clicked */
  onClear?: () => void;
  /** Additional class names */
  className?: string;
  /** Show empty state when no logs */
  showEmptyState?: boolean;
}

const textColorClasses = {
  white: 'text-gray-100',
  green: 'text-green-400',
  amber: 'text-amber-400',
};

const bgColorClasses = {
  black: 'bg-black',
  gray: 'bg-gray-900',
  dark: 'bg-gray-800',
};

export function ConsoleDisplay({
  logs,
  height = 300,
  showLineNumbers = false,
  textColor = 'green',
  bgColor = 'gray',
  title = 'Console',
  onClear,
  className,
  showEmptyState = true,
}: ConsoleDisplayProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [copied, setCopied] = React.useState(false);
  // Auto-scroll "grudado" no fim. Mantido em ref (não state) para que o efeito de novos logs
  // sempre leia o valor atual sem precisar entrar nas dependências.
  const stickToBottomRef = React.useRef(true);

  // Distância (px) até o fim abaixo da qual consideramos o usuário "no final". Pequena
  // tolerância porque scrollHeight/scrollTop podem ter frações de pixel.
  const BOTTOM_THRESHOLD = 16;

  // Atualiza o estado de "grudado" conforme o usuário rola: ao subir, desativa o auto-scroll;
  // ao voltar até o fim, reativa.
  const handleScroll = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= BOTTOM_THRESHOLD;
  }, []);

  // Auto-scroll para o fim quando novos logs chegam — só enquanto estiver grudado no fim.
  // logs é um gatilho intencional (o efeito só lê refs); por isso a supressão da regra, que
  // não distingue dependência-de-leitura de dependência-de-disparo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: logs é gatilho de re-execução
  React.useEffect(() => {
    if (stickToBottomRef.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs]);

  // Copy logs to clipboard
  const handleCopy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(logs.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
    }
  }, [logs]);

  // Ao limpar, religa o auto-scroll — caso o usuário tivesse subido o scroll antes.
  const handleClear = React.useCallback(() => {
    stickToBottomRef.current = true;
    onClear?.();
  }, [onClear]);

  // Format height value
  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  return (
    <div className={cn('rounded-lg border border-gray-700 overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-300">{title}</span>
          {logs.length > 0 && <span className="text-xs text-gray-500">({logs.length} linhas)</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            disabled={logs.length === 0}
            className="h-7 px-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700"
          >
            {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          </Button>
          {onClear && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={logs.length === 0}
              className="h-7 px-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Console Content */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className={cn(bgColorClasses[bgColor], 'overflow-auto font-mono text-xs leading-relaxed')}
        style={{ height: heightStyle }}
      >
        {logs.length === 0 && showEmptyState ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Terminal className="h-8 w-8 mb-2" />
            <p className="text-sm">Aguardando dados...</p>
          </div>
        ) : (
          <div className="p-3">
            {logs
              .map((line, index) => `${line}-${index}`)
              .map((key, index) => (
                <div key={key} className="flex">
                  {showLineNumbers && (
                    <span className="select-none text-gray-600 w-10 shrink-0 text-right pr-3">
                      {index + 1}
                    </span>
                  )}
                  <span
                    className={cn(textColorClasses[textColor], 'whitespace-pre-wrap break-all')}
                  >
                    {logs[index]}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsoleDisplay;
